import * as React from 'react';
import styled from 'styled-components';
import { useDispatch } from 'react-redux';
import { format } from 'date-fns';
import { parseApiDateTime } from '../../utils/parseApiDateTime';
import DashboardLayout from '../../layouts/DashboardLayout';
import ActionsPanel from '../../organisms/ActionsPanel';
import useTablePage from '../../hooks/useTablePage';
import {
  getGlobalVariableList,
  createGlobalVariable,
  updateGlobalVariable,
  deleteGlobalVariable,
  GlobalVariableType,
} from '../../api/globalVariables';
import GlobalVariableForm from '../../forms/GlobalVariableForm';
import Table from '../../organisms/Table';
import {
  ModalDefaultDraggable,
  ModalDeleteConfirmation,
} from '../../molecules';
import ModalInfo from '../../molecules/ModalInfo';
import TruncatedTextCell from '../../molecules/TruncatedTextCell';
import { apiSlice } from '../../redux/rtkQuery/apiSlice';

const columnsOrder = ['name', 'value', 'updatedAt'];

/**
 * Settings > Global Variables, Super Admin only.
 *
 * Values an inspection form needs that no inspection, permit or user field
 * holds. Each variable is offered as a mapping in Regular Forms (Form Setup)
 * and the Inspection Form Builder under "Global variables", and prints its
 * current value -- so changing a value here changes every form that uses it.
 */
const GlobalVariablesPage = () => {
  const dispatch = useDispatch();
  const {
    setSearch,
    handleAdd,
    search,
    tableManuallyUpdate,
    isModalOpen,
    values,
    handleEdit,
    handleDelete,
    errors,
    createOrUpdate,
    selectedItem,
    setSelectedItem,
    updateTable,
    showErrorModal,
    showSuccessModal,
    handleCloseModal,
    handleCloseModalInfo,
    modalError,
    modalErrorTitle,
    modalSuccess,
    modalTitle,
    isApiSuccess,
  } = useTablePage<GlobalVariableType>({
    createEndpoint: createGlobalVariable,
    updateEndpoint: updateGlobalVariable,
    modalCreateTitle: 'Add Global Variable',
    modalUpdateTitle: 'Edit Global Variable',
  });

  const [deleteError, setDeleteError] = React.useState('');

  // The Form Builder keeps its mapping list for the whole session
  // (apiFormbuilderSlice getFormFieldMappings). Drop it whenever the list of
  // variables changes, so the next field card that opens offers the new one.
  const refreshFormMappings = () =>
    dispatch(apiSlice.util.invalidateTags(['formFieldMappings']));

  React.useEffect(() => {
    if (isApiSuccess) {
      refreshFormMappings();
    }
  }, [isApiSuccess]);

  /**
   * Not useTablePage's `deleteById`: its catch block is commented out, so a
   * failure would close the dialog and show nothing.
   */
  const confirmDelete = (variable: GlobalVariableType) =>
    deleteGlobalVariable(variable.id)
      .then(() => {
        setSelectedItem(null);
        updateTable();
        refreshFormMappings();
        showSuccessModal(true);
      })
      .catch(error => {
        const data = error?.response?.data;
        setSelectedItem(null);
        setDeleteError(
          data?.nonFieldErrors?.[0] ||
            data?.detail ||
            'This variable could not be deleted.',
        );
        showErrorModal(true);
      });

  const columns = column => {
    switch (column) {
      case 'name':
        return 'Name of Field';
      case 'value':
        return 'Value';
      case 'updatedAt':
        return 'Last Updated';
      default:
        return null;
    }
  };

  const formatCell = (value: any, column?: string) => {
    if (column === 'value') {
      return <TruncatedTextCell value={value} />;
    }
    if (column === 'updatedAt') {
      // DRF renders "09/21/2026, 09:24:15 +0000", which date-fns cannot parse
      // directly -- hence parseApiDateTime.
      const parsed = parseApiDateTime(value);
      return parsed ? format(parsed, 'MM/dd/yyyy h:mm a') : value;
    }
    return value;
  };

  const handleCloseForm = () => {
    if (!modalError && !modalSuccess) {
      handleCloseModal();
    }
  };

  return (
    <DashboardLayout>
      <PageWrapper>
        <Intro>
          <Title>Global Variables</Title>
          <Subtitle>
            Values used on inspection forms that are not stored on
            the inspection or permit. Each one can be mapped in
            Regular Forms and the Form Builder.
          </Subtitle>
        </Intro>

        <ActionsPanel onSearch={setSearch} onAdd={handleAdd} />

        <Container>
          <Table
            customSearch
            searchQuery={search}
            columns={columns}
            columnsOrder={columnsOrder}
            rows={getGlobalVariableList}
            onEdit={handleEdit}
            onDelete={handleDelete}
            formatValue={formatCell}
            manuallyUpdate={tableManuallyUpdate}
          />
        </Container>

        {isModalOpen && (
          <ModalDefaultDraggable
            onClose={handleCloseForm}
            title={modalTitle}
          >
            <GlobalVariableForm
              initialValues={values}
              initialErrors={errors}
              modalError={modalErrorTitle.length > 0}
              onSubmit={createOrUpdate}
            />
          </ModalDefaultDraggable>
        )}

        {selectedItem && (
          <ModalDeleteConfirmation
            onCancel={() => setSelectedItem(null)}
            onConfirm={() => confirmDelete(selectedItem)}
          >
            <strong>{selectedItem.name}</strong>
            <DeleteNote>
              Forms mapped to this variable will print blank there.
            </DeleteNote>
          </ModalDeleteConfirmation>
        )}

        {(modalSuccess || modalError) && (
          <ModalInfo
            timeToClose={5000}
            isError={modalError}
            onClose={() => {
              setDeleteError('');
              handleCloseModalInfo();
            }}
            errorMsg={deleteError || modalErrorTitle}
          />
        )}
      </PageWrapper>
    </DashboardLayout>
  );
};

const PageWrapper = styled.div`
  width: 100%;
  padding: 20px 16px 0;
  box-sizing: border-box;
  font-family: 'Segoe UI', -apple-system, BlinkMacSystemFont,
    'Helvetica Neue', Arial, Roboto, sans-serif,
    'Apple Color Emoji', 'Segoe UI Emoji', 'Segoe UI Symbol';

  @media (max-width: 840px) {
    padding: 12px 12px 0;
  }
`;

const Intro = styled.div`
  margin-bottom: 20px;
`;

const Title = styled.h1`
  margin: 0;
  font-size: 1.125rem;
  font-weight: 700;
  color: var(--mainText);
`;

const Subtitle = styled.p`
  margin: 4px 0 0;
  font-size: 0.875rem;
  color: var(--neutral-500);
`;

const Container = styled.div`
  margin: 20px 0 0 0;
`;

const DeleteNote = styled.p`
  margin: 8px 0 0;
  font-size: 0.8125rem;
  color: var(--neutral-500);
`;

export default GlobalVariablesPage;
